import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, Package,
  Users, BarChart3, ArrowUpRight, ArrowDownRight, RefreshCw,
  Building2, PieChart
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminInventory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [margins, setMargins] = useState(null);
  const [monthlyTrend, setMonthlyTrend] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [dashboardRes, marginsRes, trendRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/inventory/dashboard`, { headers }),
        fetch(`${API_URL}/api/admin/inventory/margins`, { headers }),
        fetch(`${API_URL}/api/admin/inventory/monthly-trend?months=6`, { headers })
      ]);

      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (marginsRes.ok) setMargins(await marginsRes.json());
      if (trendRes.ok) setMonthlyTrend(await trendRes.json());
    } catch (error) {
      console.error('Failed to fetch inventory data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-[#D4AF37]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0d0d0d]">Inventory & Margins</h1>
            <p className="text-[#737373] mt-1">Track costs, margins, and profitability</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/admin/vendors')}>
              <Building2 className="w-4 h-4 mr-2" />
              Manage Vendors
            </Button>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Alert Cards */}
        {dashboard && (dashboard.alerts.packages_need_cost > 0 || dashboard.alerts.at_risk_packages > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dashboard.alerts.packages_need_cost > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">Missing Cost Data</p>
                  <p className="text-sm text-amber-700">{dashboard.alerts.packages_need_cost} packages don&apos;t have cost prices set</p>
                </div>
              </div>
            )}
            {dashboard.alerts.at_risk_packages > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800">At-Risk Packages</p>
                  <p className="text-sm text-red-700">{dashboard.alerts.at_risk_packages} packages may result in loss at partner pricing</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-lg border border-[#e5e5e5]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#737373]">This Month Revenue</p>
                  <p className="text-2xl font-bold text-[#0d0d0d] mt-1">{formatCurrency(dashboard.this_month.revenue)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-[#737373] mt-2">{dashboard.this_month.order_count} orders</p>
            </div>

            <div className="bg-white p-5 rounded-lg border border-[#e5e5e5]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#737373]">This Month Cost</p>
                  <p className="text-2xl font-bold text-[#0d0d0d] mt-1">{formatCurrency(dashboard.this_month.cost)}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-lg border border-[#e5e5e5]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#737373]">This Month Profit</p>
                  <p className={`text-2xl font-bold mt-1 ${dashboard.this_month.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(dashboard.this_month.profit)}
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${dashboard.this_month.profit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  {dashboard.this_month.profit >= 0 ? (
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  ) : (
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  )}
                </div>
              </div>
              <p className="text-xs text-[#737373] mt-2">{dashboard.this_month.margin_percent}% margin</p>
            </div>

            <div className="bg-white p-5 rounded-lg border border-[#e5e5e5]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#737373]">Active Vendors</p>
                  <p className="text-2xl font-bold text-[#0d0d0d] mt-1">{dashboard.vendors.active}</p>
                </div>
                <div className="p-3 bg-[#D4AF37]/10 rounded-lg">
                  <Building2 className="w-6 h-6 text-[#D4AF37]" />
                </div>
              </div>
              <p className="text-xs text-[#737373] mt-2">{dashboard.packages.total} packages</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-[#e5e5e5]">
          <div className="flex gap-6">
            {['overview', 'margins', 'at-risk'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-[#D4AF37] text-[#0d0d0d]'
                    : 'border-transparent text-[#737373] hover:text-[#0d0d0d]'
                }`}
              >
                {tab === 'overview' && 'Monthly Trend'}
                {tab === 'margins' && 'Package Margins'}
                {tab === 'at-risk' && `At-Risk (${margins?.summary?.at_risk_count || 0})`}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && monthlyTrend && (
          <div className="bg-white rounded-lg border border-[#e5e5e5] p-6">
            <h3 className="font-semibold text-[#0d0d0d] mb-4">6-Month Trend</h3>
            <div className="space-y-4">
              {monthlyTrend.trends.map((month, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-20 text-sm text-[#737373]">{month.month}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div 
                        className="h-4 bg-[#D4AF37] rounded"
                        style={{ width: `${(month.revenue / Math.max(...monthlyTrend.trends.map(t => t.revenue || 1))) * 100}%` }}
                      />
                      <span className="text-sm font-medium">{formatCurrency(month.revenue)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div 
                        className={`h-2 rounded ${month.profit >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.abs(month.profit) / Math.max(...monthlyTrend.trends.map(t => Math.abs(t.profit) || 1)) * 60}%` }}
                      />
                      <span className={`text-xs ${month.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {month.profit >= 0 ? '+' : ''}{formatCurrency(month.profit)} ({month.margin_percent}%)
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-[#737373]">{month.order_count} orders</div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-[#e5e5e5] grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-[#737373]">Total Revenue (6 mo)</p>
                <p className="text-xl font-bold text-[#0d0d0d]">{formatCurrency(monthlyTrend.summary.total_revenue)}</p>
              </div>
              <div>
                <p className="text-sm text-[#737373]">Total Profit (6 mo)</p>
                <p className={`text-xl font-bold ${monthlyTrend.summary.total_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(monthlyTrend.summary.total_profit)}
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'margins' && margins && (
          <div className="bg-white rounded-lg border border-[#e5e5e5] overflow-hidden">
            <div className="p-4 border-b border-[#e5e5e5] flex justify-between items-center">
              <div>
                <span className="text-sm text-[#737373]">Average Margin: </span>
                <span className="font-semibold text-[#0d0d0d]">{margins.summary.avg_margin_percent}%</span>
              </div>
              <div className="text-sm text-[#737373]">
                {margins.packages.length} packages
              </div>
            </div>
            <table className="w-full" data-testid="margins-table">
              <thead className="bg-[#f5f5f5]">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#525252]">Package</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-[#525252]">Vendor</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-[#525252]">Cost</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-[#525252]">Selling</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-[#525252]">Margin</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-[#525252]">Partner Min</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-[#525252]">Partner Margin</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-[#525252]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e5e5e5]">
                {margins.packages.slice(0, 50).map((pkg) => (
                  <tr key={pkg.package_id} className={`hover:bg-[#fafafa] ${pkg.is_at_risk ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#0d0d0d] text-sm">{pkg.package_name}</p>
                      <p className="text-xs text-[#737373]">{pkg.category}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#525252]">{pkg.vendor_name}</td>
                    <td className="px-4 py-3 text-sm text-right text-[#525252]">
                      {pkg.cost_price > 0 ? formatCurrency(pkg.cost_price) : <span className="text-amber-600">Not set</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(pkg.selling_price)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-medium ${pkg.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(pkg.margin)}
                      </span>
                      <span className="text-xs text-[#737373] ml-1">({pkg.margin_percent}%)</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-[#737373]">{formatCurrency(pkg.partner_price_min)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-medium ${pkg.partner_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(pkg.partner_margin)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {pkg.is_at_risk ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                          <AlertTriangle className="w-3 h-3" />
                          At Risk
                        </span>
                      ) : pkg.cost_price === 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                          No Cost
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                          OK
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'at-risk' && margins && (
          <div className="bg-white rounded-lg border border-[#e5e5e5] p-6">
            <div className="flex items-start gap-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <div>
                <h3 className="font-semibold text-[#0d0d0d]">At-Risk Packages</h3>
                <p className="text-sm text-[#737373]">
                  These packages will result in a loss if sold to partners at maximum discount (18%)
                </p>
              </div>
            </div>

            {margins.packages.filter(p => p.is_at_risk).length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-[#0d0d0d] font-medium">All packages are profitable!</p>
                <p className="text-sm text-[#737373]">No packages are at risk at partner pricing</p>
              </div>
            ) : (
              <div className="space-y-4">
                {margins.packages.filter(p => p.is_at_risk).map((pkg) => (
                  <div key={pkg.package_id} className="border border-red-200 bg-red-50 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-[#0d0d0d]">{pkg.package_name}</h4>
                        <p className="text-sm text-[#737373]">{pkg.vendor_name} • {pkg.category}</p>
                      </div>
                      <span className="text-red-600 font-bold">
                        -{formatCurrency(Math.abs(pkg.partner_margin))} loss
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-[#737373]">Cost Price</p>
                        <p className="font-medium">{formatCurrency(pkg.cost_price)}</p>
                      </div>
                      <div>
                        <p className="text-[#737373]">Selling Price</p>
                        <p className="font-medium">{formatCurrency(pkg.selling_price)}</p>
                      </div>
                      <div>
                        <p className="text-[#737373]">Partner Min (18% off)</p>
                        <p className="font-medium">{formatCurrency(pkg.partner_price_min)}</p>
                      </div>
                      <div>
                        <p className="text-[#737373]">Suggested Min Price</p>
                        <p className="font-medium text-green-600">{formatCurrency(pkg.cost_price / 0.82)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
